import { usePageHeader } from "@hooks/use_page_header";
import moment from "moment-timezone";
import { useLocation } from "react-router-dom";
import { LuHeartPulse, LuPill, LuStethoscope, LuCalendar, LuFileText } from "react-icons/lu";

moment.locale("id");

type InfoRowProps = { label: string; value: React.ReactNode };
const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className="tw-flex tw-justify-between tw-items-start tw-py-2.5 tw-gap-3">
    <span className="tw-text-xs tw-text-text-secondary tw-shrink-0 tw-w-2/5">{label}</span>
    <span className="tw-text-sm tw-font-semibold tw-text-text-primary tw-text-right tw-flex-1">
      {value}
    </span>
  </div>
);

const Divider = () => <div className="tw-h-px" style={{ background: "#f1f1f1" }} />;

const severityConfig: Record<string, { bg: string; text: string; label: string }> = {
  ringan: { bg: "#f0fdf4", text: "#16a34a", label: "Ringan" },
  sedang: { bg: "#fffbeb", text: "#d97706", label: "Sedang" },
  berat:  { bg: "#fef2f2", text: "#dc2626", label: "Berat" },
};

export const HealthDetailPage = () => {
  const { state } = useLocation();
  usePageHeader("Detail Kesehatan", { bottomNavigation: "hidden" });

  const sev = severityConfig[(state?.keparahan ?? "").toLowerCase()] ?? null;

  return (
    <div className="tw-px-4 tw-pt-2 tw-pb-20 tw-space-y-3">

      {/* Hero card */}
      <div className="card-body tw-text-center tw-py-6">
        <div
          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-3xl tw-w-16 tw-h-16 tw-mb-4"
          style={{ background: "#fff1f2" }}
        >
          <LuHeartPulse className="tw-text-3xl tw-text-[#f43f5e]" />
        </div>

        <div className="tw-text-lg tw-font-bold tw-text-text-primary tw-mb-1">
          {state?.keluhan ?? "-"}
        </div>

        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-text-text-secondary">
          <LuCalendar className="tw-text-sm" />
          {moment(state?.tanggal ?? Date.now()).format("dddd, DD MMMM YYYY · HH:mm")}
        </div>

        {sev && (
          <div
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-mt-3 tw-text-xs tw-font-semibold"
            style={{ background: sev.bg, color: sev.text }}
          >
            {sev.label}
          </div>
        )}
      </div>

      {/* Detail card */}
      <div className="card-body">
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
          <div className="tw-w-1 tw-h-4 tw-rounded-full tw-bg-primary" />
          <span className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest">
            Rincian Pemeriksaan
          </span>
        </div>

        <InfoRow
          label="Pemeriksa"
          value={
            <span className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
              <LuStethoscope className="tw-text-text-secondary tw-shrink-0" />
              {state?.pemeriksa ?? "-"}
            </span>
          }
        />
        <Divider />
        <InfoRow
          label="Obat diberikan"
          value={
            <span className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
              <LuPill className="tw-text-text-secondary tw-shrink-0" />
              {state?.obat ?? "-"}
            </span>
          }
        />

        {state?.catatan && state.catatan !== "-" && (
          <>
            <Divider />
            <div className="tw-pt-2.5 tw-pb-1">
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
                <LuFileText className="tw-text-sm tw-text-text-secondary" />
                <span className="tw-text-xs tw-text-text-secondary">Catatan</span>
              </div>
              <p className="tw-text-sm tw-font-semibold tw-text-text-primary tw-leading-relaxed">
                {state.catatan}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
